export const meta = {
  id: '49-targaryens-daenerys',
  order: 49,
  titleFi: 'Targaryenien kieli I: Daenerys Astaporissa',
  titleEn: 'The Targaryen tongue I: Daenerys at Astapor',
  lemmaIds: ['belmon', 'ānogar', 'uēpa', 'glaeson', 'rȳ', 'dāerves', 'belmurtys', 'obūljarion', 'remio', 'vējes', 'ours:mīvarrīlaksir', 'ours:italīha', 'ours:italīhy', 'ours:englandīha', 'ours:englandīhy', 'ours:japānīha', 'ours:japānīhy', 'ours:kīnīha', 'ours:kīnīhy', 'ours:farāsīha', 'ours:farāsīhy', 'ours:germānīha', 'ours:germānīhy', 'ours:russīha', 'ours:russīhy', 'ours:amerikīha', 'ours:amerikīhy', 'ours:meksikīha', 'ours:meksikīhy', 'ours:briti', 'ours:indiani', 'ours:jehudi', 'ours:kristiani', 'ours:admirali'],
  exampleIds: ['got-s3e04-004', 'got-s3e04-006', 'got-s3e04-008', 'got-s3e04-009', 'got-s3e04-011', 'got-s3e04-012', 'got-s3e04-013', 'got-s3e04-014', 'got-s3e07-001', 'got-s3e07-002', 'got-s4e03-003', 'ex-1031fd84582e'],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Verb_Moods',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Pronouns',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Derivational_Affixes',
    'https://wiki.languageinvention.com/index.php?title=Sytivīlībagon',
    'https://wiki.languageinvention.com/index.php?title=Hae',
    'https://wiki.languageinvention.com/index.php?title=Lodaor',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  showLines: ['got-s3e04-004', 'got-s3e04-006', 'got-s3e04-008', 'got-s3e04-009', 'got-s3e04-011', 'got-s3e04-012', 'got-s3e04-013', 'got-s3e04-014', 'got-s3e07-001', 'got-s3e07-002', 'got-s4e03-003'],
  pronunciation: { focus: 'rhythm', words: ['Jelmāzmo', 'zaldrīzes', 'kīvio'], lines: ['got-s3e04-008', 'got-s3e04-013', 'got-s3e04-006', 'got-s4e03-003'] },
  letter: {
    fi: 'Kirjoita hahmosi esittely: nimi, suku, arvonimet ja mistä tulet.',
    en: 'Write your character’s introduction: name, house, titles and where you come from.',
  },
  scene: 'got-s7e02-1',
  // the Halloween script (docs/COURSE.md "The Halloween script")
  // the dress rehearsal: from this lesson every approved Halloween line whose words are released is in the script
  halloween: ['esitys', 'rehearsal'],
  // Halloween-mittari (docs/COURSE.md): the meter's sentence on this week's hub
  halloweenMeter: true,
}

# Targaryenien kieli I: Daenerys Astaporissa

Tällä oppitunnilla ei opetella uutta kielioppia. Sen sijaan luetaan yksi kokonainen kohtaus Game of Thronesin kolmannen kauden neljännestä jaksosta. Moni pitää sitä korkeavalyrian tunnetuimpana hetkenä ruudulla. Daenerys on juuri antanut lohikäärmeensä orjakauppiaalle Tahrattomien armeijan hinnaksi. Kauppias on koko ajan puhunut hänestä alentuvasti omalla murteellaan siinä uskossa, ettei Daenerys ymmärrä. Sitten Daenerys alkaa puhua – korkeavalyriaa.

<Note>
**Miksi kauppias puhuu eri tavalla?** Hän puhuu Astaporin valyriaa, korkeavalyrian myöhäistä jälkeläistä. Siinä on vain kaksi sukua: korkeavalyrian kuu- ja aurinkosuku ovat yhtä, maa- ja vesisuku toista. Lisäksi siinä on artikkelit ja toisenlainen sanajärjestys, eikä pitkiä vokaaleja ole. Sama kahtiajako näkyy korkeavalyriassakin: <Term id="adjektiiviluokka">II- ja III-luokan</Term> adjektiiveilla on yksi muoto kuu- ja aurinkosuvulle ja toinen maa- ja vesisuvulle. Taustan kertoo Damien Halme (Academy of High Valyrian). Kauppiaan repliikit eivät ole korkeavalyriaa, joten niitä ei kannata käyttää mallina.
</Note>

**Näin luet:** kuuntele ensin repliikki (jos ääni on saatavilla), lue se sitten ääneen ja käy läpi sanasta sanaan -käännös. Kun olet käynyt kaikki repliikit läpi, palaa alkuun ja lue koko kohtaus putkeen ilman apuja. Tavoite on, että toisella kerralla ymmärrät jokaisen lauseen ilman käännöstä.

## 1. Käsky

<Line id="got-s3e04-004" />

<Interlinear hv="Dovaogēdys! Naejot memēbātās! Kelītīs!" gloss="Tahraton-VOC.PL eteen marssia-IMP.2PL pysähtyä-IMP.2PL" fi="Tahrattomat! Eteenpäin mars! Seis!" />

Kolme komentoa. <Hv glossed>Dovaogēdys</Hv> on vokatiivi sanasta <Hv glossed>Dovaogēdy</Hv> ’Tahraton’. Sana on johdettu kieltoetuliitteellä <Hv>dor-</Hv> (<LessonLink n={48}>oppitunti 48</LessonLink>) sanasta <Hv glossed>vaogēdy</Hv> ’tahrattu (henkilö)’, joka puolestaan tulee verbin <Hv glossed>vaogemagon</Hv> ’tahrata’ perfektin partisiipista (<LessonLink n={40}>oppitunti 40</LessonLink>). Monikon imperatiivin päätteet ovat tuttuja Duolingon käskyosiosta (<LessonLink n={21}>oppitunti 21</LessonLink>): konsonanttivartaloissa pääte on <Hv>-ātās</Hv> (<Hv>memēbātās</Hv>), vokaalivartaloissa vartalon vokaali pitenee (<Hv>kelītīs</Hv>). Tahrattomat tottelevat, ja kauppias tajuaa, että armeija on nyt Daenerysin.

## 2. Lohikäärme ei ole orja

<Line id="got-s3e04-006" />

<Interlinear hv="Zaldrīzes buzdari iksos daor." gloss="lohikäärme-NOM orja-NOM olla-PRS.SBJV.3SG ei" fi="Lohikäärme ei ole orja." />

Kun orjakauppias valittaa, että lohikäärme ei tottele, Daenerys vastaa tällä lauseella. Duolingosta tuttu sääntö: <Hv>daor</Hv> vaatii subjunktiivin, joten verbi on <Hv>iksos</Hv> eikä <Hv l="sagon">issa</Hv>. Lause toistuu sarjassa myöhemmin vielä kerran (kausi 7).

## 3. Kuka hän on

<Line id="got-s3e04-008" />

<Interlinear hv="Nyke Daenerys Jelmāzmo hen Targārio Lentrot, hen Valyrio Uēpo ānogār iksan." gloss="minä-NOM Daenerys-NOM myrsky-GEN -sta Targaryen-GEN suku-LOC -sta Valyria-GEN vanha-GEN veri-LOC olla-PRS.1SG" fi="Minä olen Daenerys Myrskysyntyinen Targaryenin suvusta, Vanhan Valyrian verta." />

<Interlinear hv="Valyrio muño ēngos ñuhys issa." gloss="Valyria-GEN äiti-GEN kieli-NOM minun-SOL.NOM olla-PRS.3SG" fi="Valyria on äidinkieleni." />

Näistä lauseista on paljon opittavaa:

- <Hv>Nyke</Hv> on painottava subjektipronomini (<LessonLink n={47}>oppitunti 47</LessonLink>): *minä*, en joku muu.
- <Hv glossed>Jelmāzmo</Hv> on genetiivi sanasta <Hv glossed>jelmāzma</Hv> ’myrsky’, joka on augmentatiivi sanasta ’tuuli’ (<LessonLink n={48}>oppitunti 48</LessonLink>). Daenerys on siis sanatarkasti *Daenerys Myrskyn*.
- <Hv glossed>hen</Hv> + lokatiivi tarkoittaa ’jostakin, jonkin piiristä’: <Hv fi="… suvusta">hen … Lentrot</Hv>, <Hv fi="… verestä">hen … ānogār</Hv>.
- <Hv fi="Vanhan Valyrian">Valyrio Uēpo</Hv>: adjektiivi on substantiivin jäljessä (<LessonLink n={47}>oppitunti 47</LessonLink>).
- Toisessa lauseessa <Hv glossed>muño ēngos ñuhys</Hv> on sanatarkasti ’äidin kieleni’. Myös omistusadjektiivi on jälkiasemassa.

## 4. Käskyt Tahrattomille

<Line id="got-s3e04-009" />

<Interlinear hv="Āeksia ossēnātās, mentī ossēnātās, qilōni pilos lue vale tolvie ossēnātās, yn riñe dōre ōdrikātās." gloss="isäntä-ACC.PL tappaa-IMP.2PL sotilas-ACC.PL tappaa-IMP.2PL ruoska-ACC pitää-PRS.3SG joka-LUN.ACC mies-ACC jokainen-LUN.ACC tappaa-IMP.2PL mutta lapsi-ACC ei.yhtään-LUN.ACC vahingoittaa-IMP.2PL" fi="Tappakaa isännät, tappakaa sotilaat, tappakaa jokainen mies, joka pitää ruoskaa, mutta älkää vahingoittako yhtäkään lasta." />

Tässä on <LessonLink n={46}>oppitunnilla 46</LessonLink> opittu relatiivilause tositilanteessa: <Hv glossed>qilōni pilos lue vale</Hv>, *ruoskaa pitää joka miehen*. <Hv>Lue</Hv> on akkusatiivissa, koska päälauseessa mies on se, joka tapetaan. <Hv glossed>Tolvie</Hv> ’jokainen’ on substantiivin jäljessä, mikä tekee käskystä painokkaan.

Lopussa ei ole kieltoa <Hv>daor</Hv> vaan kieltävä adjektiivi <Hv glossed>dōre</Hv> ’ei yhtään’, joten verbi voi olla tavallinen imperatiivi. Suomeksi käsky käännetään kieltona (*älkää vahingoittako*). Valyriassa käsky on myönteinen, ja kielto on objektissa: *vahingoittakaa ei-yhtään lasta*.

Repliikin toinen lause (<Hv>Urnēt luo buzdaro tolvio belma pryjātās!</Hv>) käytiin läpi <LessonLink n={46}>oppitunnilla 46</LessonLink>. Siinä <Hv l="lua">luo</Hv> on genetiivissä, koska orja on kahleiden omistaja.

## 5. Drakarys

<Line id="got-s3e04-011" />

*Drakarys* on kirjoista peräisin oleva sana, joka tarkoittaa lohikäärmetulta. Drogon polttaa orjakauppiaan.

## 6. Vapaus

<Line id="got-s3e04-012" />

<Interlinear hv="Jevo glaesoti rȳ buzdari istiat." gloss="teidän-GEN elämä-GEN.PL läpi orja-NOM.PL olla-PST.HAB.2PL" fi="Koko elämänne ajan olette olleet orjia." />

<Hv glossed>Rȳ</Hv> ’läpi, halki, välissä’ on postpositio, jonka edellä on genetiivi: *elämienne läpi*. Verbi <Hv>istiat</Hv> on menneessä toistuvassa: näin on ollut aina. Toinen lause, <Hv>Kesȳ tubī jemot dāervī tepan</Hv>, oli sanajärjestyksen esimerkkinä <LessonLink n={47}>oppitunnilla 47</LessonLink>: aika, epäsuora objekti, suora objekti ja verbi.

## 7. Lupaus

<Line id="got-s3e04-013" />

Ensimmäinen lause oli esimerkkinä <LessonLink n={46}>oppitunnilla 46</LessonLink>: *jokainen mies, joka haluaa lähteä, saa lähteä*. Sen jatko on kieliopillisesti kiinnostava:

<Interlinear hv="se daorys ziry ōdrikilza." gloss="ja ei.kukaan-NOM hän-ACC vahingoittaa-FUT.3SG" fi="eikä kukaan vahingoita häntä." />

Wikin tapaluokkasivu käyttää juuri tätä lausetta todisteena siitä, että subjunktiivia vaatii nimenomaan sana <Hv>daor</Hv>. Muiden kieltävien sanojen, kuten <Hv glossed>daorys</Hv> ’ei kukaan’, kanssa verbi on indikatiivissa: <Hv>ōdrikilza</Hv>, ei subjunktiivi. Suomessa kielto on aina verbissä (*ei vahingoita*), valyriassa se voi olla subjektissa.

<Interlinear hv="Jemot kīvio ñuhe tepan." gloss="te-DAT lupaus-ACC minun-LUN.ACC antaa-PRS.1SG" fi="Annan teille sanani." />

Omistusadjektiivi on taas jälkiasemassa (<Hv fi="sanani">kīvio ñuhe</Hv>), ja lause kuulostaa juhlalliselta kuin vala.

## 8. Kysymys

<Line id="got-s3e04-014" />

<Interlinear hv="Yne sytivīlībilāt? Hae dāero valoti?" gloss="minä-ACC taistella.puolesta-FUT.2PL kuin vapaa-LOC.PL mies-LOC.PL" fi="Taisteletteko puolestani? Vapaina miehinä?" />

<Pronounce word="sytivīlībilāt" />

<Hv glossed>Sytivīlībagon</Hv> ’taistella jonkun puolesta’ on rakennettu kerroksittain: <Hv>syt-</Hv> ’puolesta’ (sama kuin postpositio <Hv>syt</Hv>), <Hv>i-</Hv> (obliikvinen applikatiivi, joka tekee puolustettavasta akkusatiiviobjektin: <Hv>yne</Hv>) ja <Hv glossed>vīlībagon</Hv> ’taistella’. Wikin johdinsivu lainaa Petersonia, joka jakaa sanan osiin näin: *syt-i-vīl-īb-il-āt*. Kysymys on kyllä/ei-kysymys, joten sanajärjestys on sama kuin väitteessä (<LessonLink n={47}>oppitunti 47</LessonLink>). <Hv glossed>Hae</Hv> + lokatiivi tarkoittaa ’jonakin, jonkin tavoin’ (<Hv glossed>hae dāero valoti</Hv> ’vapaina miehinä’).

Tahrattomat vastaavat lyömällä keihäillään maahan.

## Jälkinäytös: Yunkain portilla

Kolme jaksoa myöhemmin (3×07) Daenerys on armeijoineen Yunkain edustalla ja lähettää orjakauppiaille viestin:

<Line id="got-s3e07-001" />

<Interlinear hv="Va oktio remȳti vale jikās." gloss="kohti kaupunki-GEN portti-LOC.PL mies-ACC lähettää-IMP.2SG" fi="Lähetä mies kaupungin porteille." />

<Line id="got-s3e07-002" />

<Interlinear hv="Belmurtī ivestrās kesīr pōnte jiōrinna se pōjon obūljarion mazōrīnna." gloss="orjakauppias-ACC.PL kertoa-IMP.2SG täällä he-ACC ottaa.vastaan-FUT.1SG ja heidän-TERR.ACC antautuminen-ACC hyväksyä-FUT.1SG" fi="Kerro orjakauppiaille, että otan heidät täällä vastaan ja hyväksyn heidän antautumisensa." />

Tässä on kertausta kolmesta oppitunnista. <Hv glossed>Ivestragon</Hv> ’kertoa jollekulle’ on applikatiivi, joten se, jolle kerrotaan, on akkusatiivissa (<Hv l="belmurtys">belmurtī</Hv>). Kerrottava asia on lause ilman *että*-sanaa (<LessonLink n={39}>39</LessonLink>). <Hv glossed>Belmurtys</Hv> ’orjakauppias’ on rakennettu kerroksittain (<LessonLink n={48}>48</LessonLink>): <Hv glossed>belmon</Hv> ’kahle’, verbi <Hv glossed>belmuragon</Hv> ’kahlita’ ja tekijäjohdin <Hv>-tys</Hv>. Lopuksi uhkaus alkaa sanalla <Hv glossed>lodaor</Hv> ’muuten’ (<LessonLink n={46}>46</LessonLink>): *muuten Yunkai kärsii saman kohtalon kuin Astapor*. Kohtalo on instrumentaalissa (<Hv fi="saman kohtalon">hēnkos vējose</Hv>). Vertauksessa on <Hv>hae</Hv> ja lokatiivi (<Hv fi="kuin Astapor">hae Astaprot</Hv>).

## Lue kohtaus uudelleen

Palaa nyt alkuun ja lue repliikit 1–8 putkeen ilman väliotsikoita. Kuuntele ääni, jos se on saatavilla. Kiinnitä huomiota siihen, miten muodollista Daenerysin kieli on: hän painottaa *minä*-sanaa ja antaa juhlallisesti sanansa. Hän puhuu harkiten, kuin kuningatar.

## Hahmossa: esittäydy kuin Daenerys

Halloweenina olette Targaryeneja, ja Daenerysin Astaporin repliikit ovat valmis malli sille, miten Targaryen esittäytyy ja antaa sanansa. Esittelyn lyhyin muoto kuuluu Meereenin muurien edessä: <Hv fi="Olen Daenerys Myrskysyntyinen.">Daenerys Jelmāzmo iksan.</Hv>

<Line id="got-s4e03-003" />

Pitkässä muodossa nimen perään tulevat arvonimet. Ne ovat jälkiasemassa ja juhlavia:

<Example id="ex-1031fd84582e" />

Oman esittelyn saa samasta mallista vaihtamalla nimen. Nimi pysyy nominatiivissa:

<Practice hv="Nyke Rhaenyra hen Targārio Lentrot iksan." fi="Minä olen Rhaenyra Targaryenin suvusta." />

Arvonimiä ei kannata keksiä itse. Käyttäkää niitä, jotka ovat sanakirjassa tai sarjan repliikeissä: Daenerysin arvonimet yllä ja Daemonin ja Rhaenyran arvonimet <LessonLink n={50}>oppitunnilla 50</LessonLink>.

## Ääntämisharjoitus: Peterson mallina

Kaikki tämän oppitunnin Daenerysin repliikit ovat Petersonin äänittämiä. Valitse kaksi, joita aiot käyttää hahmossa, esimerkiksi esittely (3) ja lupaus (7), ja harjoittele niitä Tämä viikko -näkymän Puhu-kohdassa: kuuntele hidastettuna ja silmukassa, nauhoita oma versiosi ja vertaa.

Kiinnitä huomiota kahteen asiaan. **Paino:** <Hv l="jelmāzma">Jelmāzmo</Hv> painottuu pitkälle tavulle <Hv part>māz</Hv>, ei ensimmäiselle tavulle. **Pituus:** sanoissa <Hv l="uēpa">Uēpo</Hv>, <Hv l="ānogar">ānogār</Hv> ja <Hv>kīvio</Hv> ensimmäisen, painollisen tavun vokaali on pitkä.

<Pronounce l="jelmāzma" word="Jelmāzmo" />

Sarjan näyttelijöiden äänitteet eivät ole ääntämisen malli: näyttelijät ääntävät esimerkiksi *y*:n usein *i*:nä ja *q*:n *k*:na. Ne ovat hyvää kuuntelumateriaalia, mutta malli on aina Peterson.

## Käsikirjoitus ääneen

Halloween-käsikirjoituksessanne on nyt kuukausien aikana kertyneet repliikit ja hyväksytyt lauseideanne. Käykää se tällä viikolla yhdessä läpi ääneen alusta loppuun. Valitkaa käsikirjoituksessa, kuka sanoo minkäkin repliikin, ja järjestäkää repliikit illan kulun mukaan: tervehdykset alkuun, maljat, pöytäkeskustelut ja puheet keskelle, hyvästit loppuun.

Tästä viikosta alkaen käsikirjoituksessa ovat mukana myös ne syys- ja lokakuun repliikit, joiden sanat olette jo oppineet, vaikka niiden oma viikko ei ole vielä tullut. Näin ehditte harjoitella illan kaikki repliikit ennen viimeistä oppituntia.

<HalloweenScript />

## Kokoa itse

Pieniä muunnelmia Daenerysin lauseista:

<Practice hv="Kesȳ tubī aōt dāervī tepan." fi="Tänään annan sinulle vapauden." />

<Practice hv="Aōt kīvio ñuhe tepan." fi="Annan sinulle sanani." />

<Vocab />

<Check />
